import React from "react";
import {
  X,
  Copy,
  Check,
  CreditCard,
  Calendar,
  CheckSquare,
  ShieldCheck,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import formatPrice from "../../../../utility/formatPrice";

interface ViewAddonModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
}

const ViewAddonModal: React.FC<ViewAddonModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  if (!isOpen || !data) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success("Copied to clipboard");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add-on Details</h2>
            <p className="text-sm text-gray-500 font-medium mt-0.5">
              {data.displayName || data.name || data.title}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopy}
              className="p-2.5 text-gray-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
              title="Copy JSON"
            >
              <Copy size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto bg-gray-100/50 flex-1 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                ID / Slug
              </p>
              <p className="text-sm font-mono text-gray-900 break-all">
                {data.id}
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                  System Name
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {data.name}
                </p>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${data.isActive !== false ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-red-50 text-red-600 border border-red-100"}`}
              >
                {data.isActive !== false ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                Base Price
              </p>
              <p className="text-lg font-bold text-gray-900 flex items-center gap-1">
                ₦{formatPrice(data.price || 0)}
                <span className="text-xs text-gray-400 font-normal">Base</span>
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                Billing Type
              </p>
              <p className="text-sm font-bold text-gray-900 uppercase">
                {data.billingType || "N/A"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                Service Type
              </p>
              <span className="inline-block mt-0.5 text-xs font-bold text-primary bg-primary/5 px-2.5 py-1 rounded-full uppercase tracking-wider">
                {data.serviceType || "DIRECT"}
              </span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">
                Category
              </p>
              <p className="text-sm font-bold text-gray-700">
                {data.category || "N/A"}
              </p>
            </div>
          </div>

          {/* Description */}
          {data.description && (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs text-gray-400 font-bold mb-2 uppercase tracking-wider">
                Description
              </p>
              <p className="text-sm text-gray-600 leading-relaxed font-medium">
                {data.description}
              </p>
            </div>
          )}

          {/* Direct Tiers Badges */}
          {(data.serviceType === "DIRECT" ||
            data.start !== undefined ||
            data.pro !== undefined) && (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-3 flex items-center gap-1.5 tracking-wider">
                <ShieldCheck size={16} className="text-emerald-500" /> Supported
                Direct Plan Tiers
              </h3>
              <div className="flex flex-wrap gap-3">
                {["start", "pro", "enterprise", "vip"].map((tier) => {
                  const isSupported = !!data[tier];
                  return (
                    <div
                      key={tier}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold uppercase transition-all ${
                        isSupported
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-gray-50 text-gray-400 border border-gray-100 line-through opacity-60"
                      }`}
                    >
                      <Check
                        size={12}
                        className={
                          isSupported ? "text-emerald-600" : "text-gray-300"
                        }
                      />
                      {tier}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Plan Specific Pricing */}
          {data.planSpecificPricing && data.planSpecificPricing.length > 0 && (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-3 flex items-center gap-1.5 tracking-wider">
                <Tag size={16} className="text-indigo-600" /> Plan-Specific
                Pricing
              </h3>
              <div className="overflow-hidden border border-gray-200 rounded-xl">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3">Plan ID</th>
                      <th className="px-4 py-3 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.planSpecificPricing.map(
                      (pricing: any, idx: number) => (
                        <tr key={idx} className="hover:bg-gray-50/50">
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">
                            {pricing.planId}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">
                            {pricing.currency || "₦"}{" "}
                            {formatPrice(pricing.price || 0)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Key Features */}
          {data.points && data.points.length > 0 && (
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-xs text-gray-400 font-bold mb-3 uppercase tracking-wider">
                Key Features
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.points.map((p: string, i: number) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-gray-600 font-medium"
                  >
                    <Check size={14} className="text-emerald-500 shrink-0" />
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw JSON Response */}
          <div className="bg-gray-50 p-6 rounded-2xl shadow-xl overflow-x-auto border border-gray-200">
            <div className="flex items-center justify-between mb-4 border-b border-gray-200 pb-3">
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                Raw JSON Object
              </h3>
            </div>
            <pre className="text-xs font-mono text-emerald-400 leading-relaxed scrollbar-thin overflow-x-auto max-h-48">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-end bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewAddonModal;
